import React, { useEffect, useRef, useState } from 'react';
import { normalizeUiTimeInput } from '../../utils/uiFormat';

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = Array.from({ length: 60 }, (_, i) => i);
const pad = (n) => String(n).padStart(2, '0');

// Segmented time field: [HH] : [MM]
// - The ":" is static (non-clickable, non-editable)
// - Click HH or MM → opens a dropdown for that segment only; pick one → it closes
// - Or type directly into a segment; typing 2 digits in HH auto-advances to MM
const UiTimeField = ({
  value,
  onChange,
  className = '',
  inputClassName = '',
  disabled = false,
}) => {
  const parse = (v) => {
    const norm = normalizeUiTimeInput(v || '');
    if (norm) {
      const [h, m] = norm.split(':');
      return { h, m };
    }
    return { h: '', m: '' };
  };

  const [hh, setHh] = useState(() => parse(value).h);
  const [mm, setMm] = useState(() => parse(value).m);
  const [openSeg, setOpenSeg] = useState(null); // 'h' | 'm' | null

  const rootRef = useRef(null);
  const hourInputRef = useRef(null);
  const minInputRef = useRef(null);
  const hourListRef = useRef(null);
  const minListRef = useRef(null);

  // Sync from external value
  useEffect(() => {
    const p = parse(value);
    setHh(p.h);
    setMm(p.m);
  }, [value]);

  // Commit current hh/mm upward
  const commit = (nextH, nextM) => {
    const h = nextH === '' ? '' : pad(Math.min(23, Math.max(0, parseInt(nextH, 10) || 0)));
    const m = nextM === '' ? '' : pad(Math.min(59, Math.max(0, parseInt(nextM, 10) || 0)));
    if (h !== '' && m !== '') {
      onChange?.(`${h}:${m}`);
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    if (!openSeg) return undefined;
    const handle = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpenSeg(null);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [openSeg]);

  // Scroll the open dropdown to the selected value
  useEffect(() => {
    if (!openSeg) return;
    const ref = openSeg === 'h' ? hourListRef : minListRef;
    const sel = openSeg === 'h' ? parseInt(hh || '0', 10) : parseInt(mm || '0', 10);
    setTimeout(() => {
      if (ref.current) ref.current.scrollTop = Math.max(0, sel * 32 - 64);
    }, 10);
  }, [openSeg, hh, mm]);

  const handleHourType = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 2);
    setHh(digits);
    if (digits.length === 2) {
      const h = pad(Math.min(23, parseInt(digits, 10) || 0));
      setHh(h);
      commit(h, mm);
      minInputRef.current?.focus();
      minInputRef.current?.select();
    }
  };

  const handleMinType = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 2);
    setMm(digits);
    if (digits.length === 2) {
      const m = pad(Math.min(59, parseInt(digits, 10) || 0));
      setMm(m);
      commit(hh, m);
    }
  };

  const pickHour = (h) => {
    const hStr = pad(h);
    setHh(hStr);
    commit(hStr, mm || '00');
    if (!mm) setMm('00');
    setOpenSeg(null);
  };

  const pickMin = (m) => {
    const mStr = pad(m);
    setMm(mStr);
    commit(hh || '00', mStr);
    if (!hh) setHh('00');
    setOpenSeg(null);
  };

  const segInputCls = `w-8 text-center bg-transparent outline-none text-sm text-gray-700 tabular-nums ${disabled ? 'cursor-not-allowed text-gray-400' : 'cursor-pointer'}`;

  const Dropdown = ({ items, selected, onPick, listRef }) => (
    <div
      ref={listRef}
      className="absolute top-full left-0 mt-1 z-50 w-16 max-h-48 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-xl"
      style={{ scrollbarWidth: 'thin' }}
    >
      {items.map((n) => (
        <div
          key={n}
          onMouseDown={(e) => { e.preventDefault(); onPick(n); }}
          className={`flex items-center justify-center text-sm font-mono cursor-pointer select-none ${
            String(selected) === String(pad(n))
              ? 'bg-primary-600 text-white font-bold'
              : 'hover:bg-primary-50 text-gray-700'
          }`}
          style={{ height: 32 }}
        >
          {pad(n)}
        </div>
      ))}
    </div>
  );

  return (
    <div ref={rootRef} className={`relative inline-flex items-center ${className}`}>
      <div className={`flex items-center rounded-xl border border-gray-200 bg-white px-2 py-2 shadow-sm focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-100 ${disabled ? 'bg-gray-50' : ''} ${inputClassName}`}>
        {/* Hour segment */}
        <div className="relative">
          <input
            ref={hourInputRef}
            type="text"
            inputMode="numeric"
            value={hh}
            placeholder="--"
            disabled={disabled}
            onChange={handleHourType}
            onFocus={(e) => { if (!disabled) { setOpenSeg('h'); e.currentTarget.select(); } }}
            onBlur={() => { if (hh !== '') setHh(pad(Math.min(23, parseInt(hh, 10) || 0))); }}
            className={segInputCls}
          />
          {openSeg === 'h' && !disabled && (
            <Dropdown items={HOURS} selected={hh} onPick={pickHour} listRef={hourListRef} />
          )}
        </div>

        {/* Static colon */}
        <span className="px-0.5 text-gray-400 font-semibold select-none">:</span>

        {/* Minute segment */}
        <div className="relative">
          <input
            ref={minInputRef}
            type="text"
            inputMode="numeric"
            value={mm}
            placeholder="--"
            disabled={disabled}
            onChange={handleMinType}
            onFocus={(e) => { if (!disabled) { setOpenSeg('m'); e.currentTarget.select(); } }}
            onBlur={() => { if (mm !== '') setMm(pad(Math.min(59, parseInt(mm, 10) || 0))); }}
            className={segInputCls}
          />
          {openSeg === 'm' && !disabled && (
            <Dropdown items={MINUTES} selected={mm} onPick={pickMin} listRef={minListRef} />
          )}
        </div>
      </div>
    </div>
  );
};

export default UiTimeField;
